import isEmpty from 'lodash/isEmpty';
import unionBy from 'lodash/unionBy';
import find from 'lodash/find';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import filter from 'lodash/filter';

import { Assets, uploadAsset } from 'modules/assets';
import {
  createAgentAssignment,
  deleteAgentAssignment,
  updateAgentAssignment,
} from 'modules/agents';
import { Mapper } from 'modules/core';
import { resolveErrorMessage } from 'modules/errors';
import { postPastRecording } from 'modules/past-recordings';

import {
  search,
  fetchTalent,
  fetchTalents,
  postTalent,
  patchTalent,
  patchSamplesOrder,
} from './api';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const searchTalents = createAsyncThunk(
  'talents/search',
  async searchOptions => await search(searchOptions),
);
export const searchTalentsForInvite = createAsyncThunk(
  'talents/searchInvite',
  async searchOptions => await search(searchOptions),
);
export const loadTalent = createAsyncThunk(
  'talents/loadSingle',
  async talentId => await fetchTalent(talentId),
);
export const loadTalents = createAsyncThunk(
  'talents/load',
  async ({ filter, pageLimit }) => await fetchTalents(filter, pageLimit),
);
export const createTalent = createAsyncThunk(
  'talents/create',
  async ({ firstName, lastName }) => await postTalent(firstName, lastName),
);

export function uploadTalentProfileImage(accountId, imageFile) {
  return dispatch => {
    const context = Assets.createAccountContext(accountId);
    return dispatch(
      uploadAsset({
        imageFile,
        assetType: 'image',
        assetClass: 'profileImage',
        context,
      }),
    );
  };
}

const update = createAsyncThunk(
  'talents/update',
  async ({ talentId, values }) => await patchTalent(talentId, values),
);
export function updateTalent(accountId, talentId, values) {
  return dispatch => {
    const { profileImage } = values;

    if (profileImage) {
      return dispatch(uploadTalentProfileImage(accountId, profileImage)).then(
        response => {
          const talentValues = {
            ...values,
            profileImage: response.payload,
          };

          return dispatch(update({ talentId, values: talentValues }));
        },
      );
    }

    return dispatch(update({ talentId, values }));
  };
}

function updateTalentAndRecordings(talentId, values) {
  const { pastRecordings } = values;

  if (isEmpty(pastRecordings)) {
    return patchTalent(talentId, values);
  }

  const createRecordings = Promise.all(
    map(pastRecordings, pastRecordingValues =>
      postPastRecording(talentId, pastRecordingValues),
    ),
  );

  return createRecordings.then(recordings => {
    const talentWithRecordings = {
      ...values,
      pastRecordingsIds: map(recordings, recording => recording.data.id),
    };

    return patchTalent(talentId, talentWithRecordings);
  });
}

export const updateTalentProfessionalInfo = createAsyncThunk(
  'talents/updateProfessionalInfo',
  async ({ talentId, values }) =>
    await updateTalentAndRecordings(talentId, values),
);

export const updateSamplesOrder = createAsyncThunk(
  'talents/updateSamplesOrder',
  async newOrder => await patchSamplesOrder(newOrder),
);

const talentsSlice = createSlice({
  name: 'talents',
  initialState: {
    searchedTalents: [],
    searchedTalentsMeta: null,
    searchedTalentsForInvite: [],
    loading: false,
    // TODO: Make obsolete (AHAB-597)
    talents: [],
    talent: null,
    latestRequest: null,
    latestTopCandidatesSearchQuery: null,
    talentsFilterData: null,
    reorderSamplesError: null,
  },
  reducers: {
    clearTalents: (state, _) => {
      state.talents = [];
    },
    setTalentsFilterData: (state, action) => {
      state.talentsFilterData = { ...action.payload };
    },
    clearTalentsFilterData: (state, _) => {
      state.talentsFilterData = null;
    },
    clearReorderSamplesError: (state, _) => {
      state.reorderSamplesError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchTalents.pending, (state, action) => {
        state.latestTopCandidatesSearchQuery = action.meta.arg.query;
        state.loading = true;
      })
      .addCase(searchTalents.fulfilled, (state, action) => {
        if (action.meta.arg.query !== state.latestTopCandidatesSearchQuery) {
          return;
        }
        const newSearchedTalents = action.payload.data;

        state.loading = false;
        state.searchedTalents = newSearchedTalents;
        state.searchedTalentsMeta = action.payload.meta;

        state.talents = unionBy(state.talents, newSearchedTalents, 'id');
      })
      .addCase(searchTalents.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(searchTalentsForInvite.pending, (state, action) => {
        state.latestRequest = action.meta.arg.query;
        state.searchingForInvite = true;
      })
      .addCase(searchTalentsForInvite.fulfilled, (state, action) => {
        // We only want to return the last query result (in e.g. a series of requests)
        if (action.meta.arg.query !== state.latestRequest) {
          return;
        }

        const newSearchedTalents = action.payload.data;
        state.searchingForInvite = false;
        state.searchedTalentsForInvite = newSearchedTalents;
        state.talents = unionBy(newSearchedTalents, state.talents, 'id');
      })
      .addCase(searchTalentsForInvite.rejected, (state, _) => {
        state.searchingForInvite = false;
      })
      .addCase(loadTalent.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadTalent.fulfilled, (state, action) => {
        const loadedTalent = action.payload.data;

        state.loading = false;
        state.talent = loadedTalent;
        state.talents = Mapper.addOrReplace(state.talents, loadedTalent);
      })
      .addCase(loadTalent.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(loadTalents.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadTalents.fulfilled, (state, action) => {
        state.loading = false;
        state.talents = action.payload.data;
      })
      .addCase(createTalent.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(createTalent.fulfilled, (state, action) => {
        const newTalent = action.payload.data;

        state.loading = false;
        state.talent = newTalent;
        state.talents = [...state.talents, newTalent];
      })
      .addCase(update.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(update.fulfilled, (state, action) => {
        const updatedTalent = action.payload.data;

        state.loading = false;
        state.talent = updatedTalent;
        state.talents = Mapper.addOrReplace(state.talents, updatedTalent);
      })
      .addCase(updateTalentProfessionalInfo.fulfilled, (state, action) => {
        const updatedTalent = action.payload.data;

        state.talent = updatedTalent;
        state.talents = Mapper.addOrReplace(state.talents, updatedTalent);
      })
      .addCase(createAgentAssignment.fulfilled, (state, action) => {
        state.talent.agentAssignments = [
          ...state.talent.agentAssignments,
          action.payload.data,
        ];
      })
      .addCase(updateAgentAssignment.fulfilled, (state, action) => {
        state.talent.agentAssignments = state.talent.agentAssignments.map(
          assignment =>
            assignment.id !== action.payload.data.id
              ? assignment
              : action.payload.data,
        );
      })
      .addCase(deleteAgentAssignment.fulfilled, (state, action) => {
        state.talent.agentAssignments = filter(
          state.talent.agentAssignments,
          assignment => assignment.id !== action.meta.arg,
        );
      })
      .addCase(updateSamplesOrder.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(updateSamplesOrder.fulfilled, (state, _) => {
        state.loading = false;
      })
      .addCase(updateSamplesOrder.rejected, (state, action) => {
        state.loading = false;
        state.reorderSamplesError = resolveErrorMessage(action.error);
      });
  },
  selectors: {
    getTalent: state => state.talent,
    getSearchedTalents: state => state.searchedTalents,
    getSearchedTalentsMeta: state => state.searchedTalentsMeta,
    getSearchingForInvite: state => state.searchingForInvite,
    getSearchedTalentsForInviteState: state => state.searchedTalentsForInvite,
    getTalents: state => state.talents,
    getTalentLoading: state => state.loading,
    getTalentsFilterData: state => state.talentsFilterData,
    getReorderSamplesError: state => state.reorderSamplesError,
  },
});

export const reducer = talentsSlice.reducer;
export const {
  clearTalents,
  setTalentsFilterData,
  clearTalentsFilterData,
  clearReorderSamplesError,
} = talentsSlice.actions;
export const {
  getTalent,
  getSearchedTalents,
  getSearchedTalentsMeta,
  getSearchingForInvite,
  getSearchedTalentsForInviteState,
  getTalents,
  getTalentLoading,
  getTalentsFilterData,
  getReorderSamplesError,
} = talentsSlice.selectors;

export const getSearchedTalentsForInvite = existingAuditionees =>
  createSelector(
    getSearchedTalentsForInviteState,
    searchedTalents => {
      return filter(
        searchedTalents,
        searchedTalent => !find(existingAuditionees, { id: searchedTalent.id }),
      );
    },
  );

export const getTalentById = talentId =>
  createSelector(
    getTalents,
    talents => find(talents, { id: talentId }),
  );

export const getTalentByAccountId = accountId =>
  createSelector(
    getTalents,
    talents => find(talents, { accountId }),
  );

export const getVisibleTalentsOnAgentsProfile = agentAssignments =>
  createSelector(
    getSearchedTalents,
    talents => {
      const visibleAssignements = filter(
        agentAssignments,
        assignement => assignement.visibleOnAgentProfile,
      );
      const visibleTalentsIds = visibleAssignements.map(
        assignement => assignement.talentId,
      );
      const visibleTalents = [];

      forEach(talents, talent => {
        const { id } = talent;
        const clientVisible = includes(visibleTalentsIds, id);
        if (clientVisible) {
          visibleTalents.push(talent);
        }
      });
      return visibleTalents;
    },
  );

export const getNotVisibleTalentsOnAgentsProfile = agentAssignments =>
  createSelector(
    getSearchedTalents,
    talents => {
      const notVisibleAssignements = filter(
        agentAssignments,
        assignement => !assignement.visibleOnAgentProfile,
      );
      const notVisibleTalentsIds = notVisibleAssignements.map(
        assignement => assignement.talentId,
      );
      const notVisibleTalents = [];

      forEach(talents, talent => {
        const { id } = talent;
        const clientNotVisible = includes(notVisibleTalentsIds, id);
        if (clientNotVisible) {
          notVisibleTalents.push(talent);
        }
      });

      return notVisibleTalents;
    },
  );
